import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  InviteModal,
  SuccessModal,
  DeleteItem,
  SuccessfulDelete,
} from "../../components/modal";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";
import DeleteImg from "../../assets/delLogo.png";
import verifyImg from "../../assets/verifyImg.png";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";

const APPROVAL_FLOWS_CACHE_KEY = "cehr_company_approval_flows_v1";

const ApprovalFlow = () => {
  const cachedData = useMemo(
    () => readSessionCache(APPROVAL_FLOWS_CACHE_KEY, { approvalFlows: [] }),
    [],
  );
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openDeleteSuccessModal, setOpenDeleteSuccessModal] = useState(false);
  const [approvalFlows, setApprovalFlows] = useState(
    cachedData?.approvalFlows || [],
  );
  const [approvalName, setApprovalName] = useState("");
  const [approvalDescription, setApprovalDescription] = useState("");
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(
    !cachedData?.approvalFlows?.length,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();

  const loadApprovalFlows = useCallback(
    async ({ silent = false } = {}) => {
      if (!apiBaseUrl) {
        setErrorMessage("API base URL is not configured.");
        return;
      }

      try {
        if (!silent && approvalFlows.length === 0) {
          setIsLoading(true);
        }
        setErrorMessage("");
        const response = await axios.get(
          `${apiBaseUrl}/organizations/approval-flows`,
        );
        const nextApprovalFlows = response?.data?.approvalFlows || [];
        setApprovalFlows(nextApprovalFlows);
        writeSessionCache(APPROVAL_FLOWS_CACHE_KEY, {
          approvalFlows: nextApprovalFlows,
        });
      } catch (error) {
        const responseMessage =
          error?.response?.data?.message ||
          "Unable to load approval flows. Please try again.";
        setErrorMessage(
          Array.isArray(responseMessage)
            ? responseMessage.join(", ")
            : responseMessage,
        );
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl, approvalFlows.length],
  );

  useEffect(() => {
    loadApprovalFlows({ silent: true });
  }, [loadApprovalFlows]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadApprovalFlows({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadApprovalFlows]);

  const resetForm = () => {
    setApprovalName("");
    setApprovalDescription("");
    setErrorMessage("");
  };

  const openCreate = () => {
    setSelectedFlow(null);
    resetForm();
    setOpenCreateModal(true);
  };

  const openEdit = (flow) => {
    setSelectedFlow(flow);
    setApprovalName(flow.approvalName || "");
    setApprovalDescription(flow.approvalDescription || "");
    setErrorMessage("");
    setOpenEditModal(true);
  };

  const openDelete = (flow) => {
    setSelectedFlow(flow);
    setErrorMessage("");
    setOpenDeleteModal(true);
  };

  const handleCreate = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    if (!approvalName.trim() || !approvalDescription.trim()) {
      setErrorMessage("Approval name and approval description are required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.post(`${apiBaseUrl}/organizations/approval-flows`, {
        approvalName: approvalName.trim(),
        approvalDescription: approvalDescription.trim(),
      });

      setOpenCreateModal(false);
      setOpenSuccessModal(true);
      await loadApprovalFlows();
      resetForm();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to create approval flow. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (ev) => {
    ev.preventDefault();

    if (!apiBaseUrl || !selectedFlow?.id) {
      setErrorMessage("Unable to update this approval flow right now.");
      return;
    }

    if (!approvalName.trim() || !approvalDescription.trim()) {
      setErrorMessage("Approval name and approval description are required.");
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage("");

      await axios.patch(
        `${apiBaseUrl}/organizations/approval-flows/${selectedFlow.id}`,
        {
          approvalName: approvalName.trim(),
          approvalDescription: approvalDescription.trim(),
        },
      );

      setOpenEditModal(false);
      setOpenSuccessModal(true);
      await loadApprovalFlows();
      resetForm();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to update approval flow. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!apiBaseUrl || !selectedFlow?.id) {
      setErrorMessage("Unable to delete this approval flow right now.");
      return;
    }

    try {
      setIsDeleting(true);
      setErrorMessage("");

      await axios.delete(
        `${apiBaseUrl}/organizations/approval-flows/${selectedFlow.id}`,
      );

      setOpenDeleteModal(false);
      setOpenDeleteSuccessModal(true);
      await loadApprovalFlows();
    } catch (error) {
      const responseMessage =
        error?.response?.data?.message ||
        "Unable to delete approval flow. Please try again.";
      setErrorMessage(
        Array.isArray(responseMessage)
          ? responseMessage.join(", ")
          : responseMessage,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const getFlowBadgeLabel = (approvalName) => {
    if (!approvalName) return "General";

    const normalized = approvalName.trim().toLowerCase();

    if (normalized.includes("cash")) return "Cash";
    if (normalized.includes("leave")) return "Leave";
    if (normalized.includes("expense")) return "Expense";
    if (normalized.includes("payment")) return "Payment";
    if (normalized.includes("request")) return "Request";

    const firstWord = approvalName.trim().split(/\s+/)[0];
    return firstWord.length > 12 ? `${firstWord.slice(0, 12)}...` : firstWord;
  };

  return (
    <div className="font-Nunito p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Approval Flows" },
        ]}
        title="Approval Flows"
        subtitle="Create approval chains and link positions to each step."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={
          <button
            onClick={openCreate}
            className="bg-[#1234CD] px-6 py-2 rounded-xl text-white"
          >
            Add new
          </button>
        }
      />

      {errorMessage && (
        <p
          className="px-6 mb-4 text-sm text-red-600"
          role="alert"
        >
          {errorMessage}
        </p>
      )}

      {!isLoading && approvalFlows.length === 0 ? (
        <div className="mx-6 my-12 rounded-2xl border border-dashed border-[#B6C4F7] bg-[#F7F9FF] px-6 py-10 text-center">
          <h3 className="text-lg font-semibold text-[#071C50]">
            No approval flow yet
          </h3>
          <p className="mt-2 text-sm text-[#5F6B8A]">
            Create your first approval flow (for example, Cash Flow) to start
            adding per-level approvers.
          </p>
          <button
            onClick={openCreate}
            className="mt-5 bg-[#1234CD] px-5 py-2 rounded-xl text-white"
          >
            Create approval flow
          </button>
        </div>
      ) : null}

      <div className="grid lg:min-w-[200px] my-8 w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {approvalFlows.map((item) => (
          <div
            key={item.id}
            className="rounded-2xl border border-[#DDE4FF] bg-white p-5 shadow-sm hover:shadow-md transition"
          >
            <button
              onClick={() => {
                sessionStorage.setItem("selectedApprovalFlowId", item.id);
                navigate("/company-settings/leave-flow", {
                  state: { approvalFlowId: item.id },
                });
              }}
              className="text-left w-full"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-semibold text-[#071C50] text-lg leading-6">
                  {item.approvalName}
                </h2>
                <span className="inline-flex items-center rounded-full bg-[#E9EEFF] text-[#1234CD] text-xs font-semibold px-2 py-1">
                  {getFlowBadgeLabel(item.approvalName)}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-600 leading-6 line-clamp-3">
                {item.approvalDescription}
              </p>
            </button>
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#EEF1FB]">
              <button
                onClick={() => openEdit(item)}
                className="text-[#1234CD] text-sm font-semibold"
              >
                Edit
              </button>
              <button
                onClick={() => openDelete(item)}
                className="text-red-600 text-sm font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {isLoading && approvalFlows.length === 0 ? (
        <FetchLoadingState message="Loading approval flows..." />
      ) : null}

      <InviteModal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
      >
        <div className="my-8 font-Nunito">
          <p className="text-2xl font-semibold text-black ">
            Create Approval Flow
          </p>
          <form
            className="my-12"
            onSubmit={handleCreate}
          >
            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh">
                Approval Name
              </label>
              <input
                type="text"
                placeholder="Title"
                value={approvalName}
                onChange={(ev) => setApprovalName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>
            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh">
                Approval Description
              </label>
              <textarea
                placeholder="Description"
                value={approvalDescription}
                onChange={(ev) => setApprovalDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>
            <div className="flex justify-center mt-20 items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] font-satoshi leading-[21.6px] w-40 rounded-xl text-white py-[16px] bg-[#1234CD] h-[40px]text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? "Creating..." : "Create"}</span>
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <InviteModal
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
      >
        <div className="my-8 font-Nunito">
          <p className="text-2xl font-semibold text-black ">
            Edit Approval Flow
          </p>
          <form
            className="my-12"
            onSubmit={handleUpdate}
          >
            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh">
                Approval Name
              </label>
              <input
                type="text"
                value={approvalName}
                onChange={(ev) => setApprovalName(ev.target.value)}
                className="w-full p-2 text-center mb-2 rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>
            <div>
              <label className="block mb-2 font-[500] text-[14px] text-black leading-[18.9px] font-satosh">
                Approval Description
              </label>
              <textarea
                value={approvalDescription}
                onChange={(ev) => setApprovalDescription(ev.target.value)}
                rows={4}
                wrap="soft"
                className="w-full p-2 min-h-36 mb-4 resize-y whitespace-pre-wrap break-words rounded-xl focus:outline-[#1234CD] border-2 border-[#F3F5F5] py-[16px]"
              />
            </div>
            <div className="flex justify-center mt-20 items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="font-[700] font-satoshi leading-[21.6px] w-40 rounded-xl text-white py-[16px] bg-[#1234CD] h-[40px]text-[16px] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? "Updating..." : "Update"}</span>
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <DeleteItem
        isOpen={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
      >
        <div>
          <img
            className="w-40 mx-auto animate-pulse"
            src={DeleteImg}
            alt="delete approval flow"
          />
          <h1 className="text-lg text-center text-[#000000] font-bold">
            Continue to delete this approval flow?
          </h1>
          <p className="text-sm text-center mt-2">Action cannot be undone.</p>
        </div>
        <div className="flex items-center py-4 gap-4 my-4">
          <button
            onClick={() => setOpenDeleteModal(false)}
            className="border-[#1233CC] py-1 border-2 text-[#1233CC] font-medium px-4 rounded-[10px] w-[102px]"
          >
            No
          </button>
          <button
            className="text-[16px] font-medium px-4 py-1 bg-[#1233CC] rounded-[10px] w-[102px] text-white disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      </DeleteItem>

      <SuccessModal
        isOpen={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
      />

      <SuccessfulDelete
        isOpen={openDeleteSuccessModal}
        onClose={() => setOpenDeleteSuccessModal(false)}
      >
        <div>
          <img
            src={verifyImg}
            alt="approval flow deleted"
            className="w-20 animate-ping mx-auto"
          />
          <div className="text-center mt-6">
            <h2 className="text-black text-[20px] leading-[27px] font-bold mb-2">
              Approval flow deleted
            </h2>
            <p className="text-[#74777B] text-[14px] leading-[18.9px]">
              The approval flow was removed successfully.
            </p>
          </div>
        </div>
      </SuccessfulDelete>
    </div>
  );
};

export default ApprovalFlow;
